import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';

// Styled components
const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  maxWidth: '100%',
  overflowX: 'auto',
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  boxShadow: 'none',
  border: '1px solid #E1E8ED',
}));

const StyledTable = styled(Table)(({ theme }) => ({
  minWidth: 1000,
  borderCollapse: 'separate',
  borderSpacing: 0,
}));

const TableWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
}));

const TableTitle = styled(Typography)(({ theme }) => ({
  fontSize: '18px',
  fontWeight: 600,
  color: '#063142',
  marginBottom: '16px',
  textAlign: 'center',
}));

const HeaderCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: '#E7EFF5',
  color: '#063142',
  fontWeight: 600,
  textAlign: 'center',
  border: '1px solid #E1E8ED',
  padding: '8px 8px',
  fontSize: '14px',
  whiteSpace: 'nowrap',
}));

const SubHeaderCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: '#E7EFF5',
  color: '#063142',
  fontWeight: 500,
  textAlign: 'center',
  border: '1px solid #E1E8ED',
  padding: '6px 4px',
  fontSize: '12px',
  whiteSpace: 'pre-line',
  lineHeight: '1.2',
  verticalAlign: 'middle',
}));

const DataCell = styled(TableCell)(({ theme }) => ({
  border: '1px solid #E1E8ED',
  padding: '6px 6px',
  textAlign: 'right',
  fontSize: '13px',
  whiteSpace: 'nowrap',
  '&.bold-bottom': {
    borderBottom: '2px solid #063142',
  },
  '&.total-row': {
    fontWeight: 700,
  },
}));

const FormatGroupCell = styled(TableCell)(({ theme }) => ({
  border: '1px solid #E1E8ED',
  padding: '6px 6px',
  textAlign: 'center',
  fontSize: '14px',
  fontWeight: 600,
  color: '#063142',
  backgroundColor: '#F8FBFF',
  verticalAlign: 'middle',
}));

const QuarterCell = styled(TableCell)(({ theme }) => ({
  border: '1px solid #E1E8ED',
  padding: '6px 6px',
  textAlign: 'center',
  fontSize: '13px',
  fontWeight: 500,
  color: '#063142',
  backgroundColor: '#FAFBFC',
  '&.bold-bottom': {
    borderBottom: '2px solid #063142',
  },
  '&.total-row': {
    fontWeight: 700,
  },
}));

// Column structure
const columnStructure = [
  { accessor: 'format', header: 'Format', width: 80 },
  { accessor: 'quarter', header: 'Quarter', width: 80 },
  { accessor: 'a_actual_weight', header: 'Actual\nweight', width: 100 },
  { accessor: 'a_gross_percent_from_sampling', header: 'Gross%\nfrom\nsampling', width: 120 },
  { accessor: 'a_ipk_from_sampling', header: 'IPK\nfrom\nsampling', width: 110 },
  { accessor: 'a_estimated_number_of_items', header: 'Estimated\nnumber\nof items', width: 130 },
  { accessor: 'b_actual_weight', header: 'Actual\nweight', width: 100 },
  { accessor: 'b_gross_percent_from_sampling', header: 'Gross%\nfrom\nsampling', width: 120 },
  { accessor: 'b_ipk_from_sampling', header: 'IPK\nfrom\nsampling', width: 110 },
  { accessor: 'b_estimated_number_of_items', header: 'Estimated\nnumber\nof items', width: 130 },
  { accessor: 'c_actual_weight', header: 'Actual\nweight', width: 100 },
  { accessor: 'c_gross_percent_from_sampling', header: 'Gross%\nfrom\nsampling', width: 120 },
  { accessor: 'c_ipk_from_sampling', header: 'IPK\nfrom\nsampling', width: 110 },
  { accessor: 'c_estimated_number_of_items', header: 'Estimated\nnumber\nof items', width: 130 },
  { accessor: 'd_actual_weight', header: 'Actual\nweight', width: 100 },
  { accessor: 'd_gross_percent_from_sampling', header: 'Gross%\nfrom\nsampling', width: 120 },
  { accessor: 'd_ipk_from_sampling', header: 'IPK\nfrom\nsampling', width: 110 },
  { accessor: 'd_estimated_number_of_items', header: 'Estimated\nnumber\nof items', width: 130 },
  { accessor: 'total_actual_weight', header: 'Actual\nweight', width: 100 },
  { accessor: 'total_gross_percent', header: 'Gross%', width: 80 },
  { accessor: 'total_ipk', header: 'IPK', width: 80 },
  { accessor: 'total_estimated_items', header: 'Estimated\nnumber\nof items', width: 130 },
];

// Column groups for grouped headers
const columnGroups = [
  {
    header: 'Mail Category A',
    columns: [
      'a_actual_weight',
      'a_gross_percent_from_sampling',
      'a_ipk_from_sampling',
      'a_estimated_number_of_items',
    ],
  },
  {
    header: 'Mail Category D',
    columns: [
      'd_actual_weight',
      'd_gross_percent_from_sampling',
      'd_ipk_from_sampling',
      'd_estimated_number_of_items',
    ],
  },
  {
    header: 'Mail Category B',
    columns: [
      'b_actual_weight',
      'b_gross_percent_from_sampling',
      'b_ipk_from_sampling',
      'b_estimated_number_of_items',
    ],
  },
  {
    header: 'Mail Category C',
    columns: [
      'c_actual_weight',
      'c_gross_percent_from_sampling',
      'c_ipk_from_sampling',
      'c_estimated_number_of_items',
    ],
  },
  {
    header: 'Total',
    columns: ['total_actual_weight', 'total_gross_percent', 'total_ipk', 'total_estimated_items'],
  },
];

// Calculate column widths
const calculateColumnWidths = (data: any[], columns: any[]) => {
  const newColumnWidths: Record<string, string> = {};
  const minColWidth = 80;
  const maxColWidth = 200;

  columns.forEach((column) => {
    let maxWidth = minColWidth;

    // Check header text width
    const headerWidth = column.header.length * 8;
    maxWidth = Math.max(maxWidth, headerWidth);

    // Check data content width
    data.forEach((row) => {
      const value = row[column.accessor];
      if (value !== null && value !== undefined) {
        const cellWidth = value.toString().length * 8;
        maxWidth = Math.max(maxWidth, cellWidth);
      }
    });

    newColumnWidths[column.accessor] = Math.min(
      Math.max(maxWidth, minColWidth),
      maxColWidth
    ).toString();
  });

  return newColumnWidths;
};

// Number formatting function - handles both formatted strings and raw numbers
const formatNumber = (value: number | string | null | undefined, columnAccessor: string): string => {
  if (value === null || value === undefined) return '';
  
  // If value is already a formatted string, return it as-is
  if (typeof value === 'string') {
    return value;
  }

  // Otherwise, format the number
  if (columnAccessor.includes('actual_weight')) {
    return value.toFixed(3);
  } else if (columnAccessor.includes('gross_percent')) {
    return `${value.toFixed(2)}%`;
  } else if (columnAccessor.includes('ipk')) {
    return value.toFixed(4);
  } else if (
    columnAccessor.includes('estimated_number_of_items') ||
    columnAccessor.includes('total_estimated_items')
  ) {
    return Math.round(value).toString();
  }

  return value.toString();
};

// Render cell content
const renderCell = (row: any, column: any) => {
  const value = row[column.accessor];

  if (column.accessor === 'format' || column.accessor === 'quarter') {
    return row[column.accessor];
  }

  return formatNumber(value, column.accessor);
};

// Check if column is numeric
const isNumericColumn = (accessor: string): boolean => {
  return !['format', 'quarter'].includes(accessor);
};

// Group data by format for grouped rows
const groupDataByFormat = (data: any[]) => {
  const groupedData: any[] = [];
  const formats = ['P/S', 'G', 'E', 'Mixed'];

  formats.forEach((format) => {
    const formatRows = data.filter((row) => row.format === format);
    if (formatRows.length > 0) {
      groupedData.push({
        format,
        rows: formatRows,
        rowSpan: formatRows.length,
      });
    }
  });

  return groupedData;
};

export interface CN54BisColumn {
  accessor: string;
  header: string;
  width: string;
}

export interface CN54BisTableProps {
  data: any[];
  moduleName?: string;
  viewMode?: boolean;
  disableAutoSizing?: boolean;
  pageSize?: number;
  lazyLoad?: boolean;
  lazyLoadHeight?: number;
  lazyLoadingBatchSize?: number;
  rowBackgroundColor?: string;
}

function CN54BisTable({
  data,
  moduleName = 'CN54Bis',
  viewMode = false,
  disableAutoSizing = false,
  pageSize = 25,
  lazyLoad = false,
  lazyLoadHeight = 400,
  lazyLoadingBatchSize = 25,
  rowBackgroundColor,
}: CN54BisTableProps) {
  const [columnWidths, setColumnWidths] = useState<Record<string, string>>({});
  const [isResizing, setIsResizing] = useState(false);
  const resizeTimeoutRef = useRef<NodeJS.Timeout>();

  // Calculate column widths on data change
  useEffect(() => {
    if (!disableAutoSizing && data.length > 0) {
      const widths = calculateColumnWidths(data, columnStructure);
      setColumnWidths(widths);
    }
  }, [data, disableAutoSizing]);

  // Group data for grouped rows
  const groupedData = useMemo(() => groupDataByFormat(data), [data]);

  // Handle column resize
  const handleColumnResize = useCallback((columnAccessor: string, newWidth: number) => {
    setIsResizing(true);

    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current);
    }

    resizeTimeoutRef.current = setTimeout(() => {
      setColumnWidths((prev) => ({
        ...prev,
        [columnAccessor]: Math.max(80, Math.min(300, newWidth)).toString(),
      }));
      setIsResizing(false);
    }, 100);
  }, []);

  if (!data || data.length === 0) {
    return (
      <TableWrapper data-testid="cn54bis-table-wrapper-empty">
        <TableTitle data-testid="cn54bis-table-title-empty">No data available</TableTitle>
      </TableWrapper>
    );
  }

  return (
    <TableWrapper data-testid="cn54bis-table-wrapper">
      <TableTitle data-testid="cn54bis-table-title">{moduleName} </TableTitle>

      <StyledTableContainer data-testid="cn54bis-table-container">
        <StyledPaper data-testid="cn54bis-table-paper">
          <StyledTable data-testid="cn54bis-table">
            <TableHead data-testid="cn54bis-table-head">
              {/* Main header row */}
              <TableRow data-testid="cn54bis-table-header-row">
                <HeaderCell style={{ width: columnWidths.format || '80px' }} data-testid="cn54bis-table-format-header">Format</HeaderCell>
                <HeaderCell style={{ width: columnWidths.quarter || '80px' }} data-testid="cn54bis-table-quarter-header">Quarter</HeaderCell>

                {columnGroups.map((group) => (
                  <HeaderCell
                    key={group.header}
                    colSpan={group.columns.length}
                    style={{ textAlign: 'center' }}
                    data-testid={`cn54bis-table-group-header-${group.header.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {group.header}
                  </HeaderCell>
                ))}
              </TableRow>

              {/* Sub-header row */}
              <TableRow data-testid="cn54bis-table-subheader-row">
                <SubHeaderCell data-testid="cn54bis-table-format-subheader"></SubHeaderCell>
                <SubHeaderCell data-testid="cn54bis-table-quarter-subheader"></SubHeaderCell>

                {columnGroups.map((group) =>
                  group.columns.map((columnAccessor) => {
                    const column = columnStructure.find((col) => col.accessor === columnAccessor);
                    return (
                      <SubHeaderCell
                        key={columnAccessor}
                        style={{ width: columnWidths[columnAccessor] || column?.width || '100px' }}
                        data-testid={`cn54bis-table-subheader-${columnAccessor}`}
                      >
                        {column?.header || columnAccessor}
                      </SubHeaderCell>
                    );
                  })
                )}
              </TableRow>
            </TableHead>

                         <TableBody data-testid="cn54bis-table-body">
               {groupedData.map((formatGroup, formatIndex) =>
                 formatGroup.rows.map((row: any, rowIndex: number) => (
                   <TableRow
                     key={`${formatGroup.format}_${rowIndex}`}
                     data-testid={`cn54bis-table-row-${formatGroup.format}-${rowIndex}`}
                                           sx={{
                        backgroundColor: rowBackgroundColor || 'inherit',
                        '&:hover': { backgroundColor: '#F5F5F5' },
                        // Add bold borders between format groups and after Total rows
                        borderBottom: (rowIndex === formatGroup.rows.length - 1 || row.quarter === 'Total') ? '2px solid #063142' : '1px solid #E1E8ED',
                      }}
                   >
                     {/* Format column with rowSpan for first row of each format group */}
                     {rowIndex === 0 && (
                       <FormatGroupCell
                         rowSpan={formatGroup.rowSpan}
                         style={{ width: columnWidths.format || '80px' }}
                         data-testid={`cn54bis-table-format-cell-${formatGroup.format}-${rowIndex}`}
                       >
                         {formatGroup.format}
                       </FormatGroupCell>
                     )}

                     {/* Quarter column */}
                     <QuarterCell 
                       className={`${(rowIndex === formatGroup.rows.length - 1 || row.quarter === 'Total') ? 'bold-bottom' : ''} ${row.quarter === 'Total' ? 'total-row' : ''}`}
                       style={{ width: columnWidths.quarter || '80px' }}
                       data-testid={`cn54bis-table-quarter-cell-${formatGroup.format}-${rowIndex}`}
                     >
                       {row.quarter}
                     </QuarterCell>

                     {/* Data columns */}
                     {columnGroups.map((group, groupIndex) =>
                       group.columns.map((columnAccessor, colIndex) => {
                         const column = columnStructure.find(
                           (col) => col.accessor === columnAccessor
                         );
                         return (
                                                       <DataCell
                              key={columnAccessor}
                              className={`${(rowIndex === formatGroup.rows.length - 1 || row.quarter === 'Total') ? 'bold-bottom' : ''} ${row.quarter === 'Total' ? 'total-row' : ''}`}
                              sx={{
                                width: columnWidths[columnAccessor] || column?.width || '100px',
                                textAlign: isNumericColumn(columnAccessor) ? 'right' : 'center',
                                // Add bold right border for the last column of each group
                                borderRight: colIndex === group.columns.length - 1 ? '2px solid #063142' : '1px solid #E1E8ED',
                              }}
                              data-testid={`cn54bis-table-data-cell-${columnAccessor}-${formatGroup.format}-${rowIndex}`}
                            >
                             {renderCell(
                               row,
                               column || { accessor: columnAccessor, header: columnAccessor }
                             )}
                           </DataCell>
                         );
                       })
                     )}
                   </TableRow>
                 ))
               )}
             </TableBody>
          </StyledTable>
        </StyledPaper>
      </StyledTableContainer>
    </TableWrapper>
  );
}

export default CN54BisTable;
